// packages/web/src/components/ReportIssueModal.tsx
// ═══════════════════════════════════════════════════════════════════
// Global "Report Error" modal — captures screenshot + user context
// ═══════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import { apiClient } from '../services/api.client';

interface ReportIssueModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const categories = [
  { value: 'bug', label: '🐛 Bug / Error' },
  { value: 'ui', label: '🎨 UI / Display Issue' },
  { value: 'data', label: '📊 Data Issue' },
  { value: 'performance', label: '⚡ Performance' },
  { value: 'feature', label: '💡 Feature Request' },
  { value: 'general', label: '📝 General' },
];

// Capture a full-page screenshot using html2canvas
async function captureScreenshot(): Promise<string | null> {
  try {
    const canvas = await html2canvas(document.body, {
      useCORS: true,
      scale: 0.5,
      logging: false,
      width: window.innerWidth,
      height: window.innerHeight,
    });
    return canvas.toDataURL('image/png', 0.6);
  } catch (err) {
    console.warn('[ReportIssue] Screenshot capture failed:', err);
    return null;
  }
}

export default function ReportIssueModal({ isOpen, onClose }: ReportIssueModalProps) {
  const [category, setCategory] = useState('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Capture screenshot when modal opens
  useEffect(() => {
    if (isOpen) {
      setSuccess(false);
      setError(null);
      // Small delay to let the modal render, then capture the page behind it
      const timer = setTimeout(async () => {
        const img = await captureScreenshot();
        setScreenshot(img);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setScreenshot(null);
      setTitle('');
      setDescription('');
      setCategory('bug');
    }
  }, [isOpen]);

  async function handleSubmit() {
    if (!title.trim() || !description.trim()) {
      setError('Please fill in title and description');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const res = await apiClient.post('/support/tickets', {
        category,
        title: title.trim(),
        description: description.trim(),
        screenshot_base64: screenshot,
        url_location: window.location.href,
      });

      if (res.success) {
        setSuccess(true);
        setTimeout(() => {
          onClose();
          setSuccess(false);
        }, 2000);
      } else {
        setError((res.error as any)?.message || 'Failed to submit report');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  }

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          zIndex: 9998, backdropFilter: 'blur(2px)',
        }}
      />

      {/* Modal */}
      <div
        ref={modalRef}
        style={{
          position: 'fixed', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          background: '#fff', borderRadius: 16, padding: '1.5rem',
          width: '90%', maxWidth: 520, maxHeight: '85vh', overflowY: 'auto',
          zIndex: 9999, boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700, color: '#1e293b' }}>
            🐛 Report an Issue
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#64748b' }}>✕</button>
        </div>

        {success ? (
          <div style={{ textAlign: 'center', padding: '2rem 0' }}>
            <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>✅</div>
            <h3 style={{ color: '#10b981', margin: '0 0 0.5rem', fontWeight: 700 }}>Report Submitted!</h3>
            <p style={{ color: '#64748b', fontSize: '0.85rem' }}>Our team will review your issue shortly.</p>
          </div>
        ) : (
          <>
            {/* Category */}
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: 4 }}>Category</label>
              <select
                value={category}
                onChange={e => setCategory(e.target.value)}
                style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: 8, border: '1px solid #d1d5db', fontSize: '0.85rem', color: '#1e293b' }}
              >
                {categories.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>

            {/* Title */}
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: 4 }}>Title *</label>
              <input
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Brief summary of the issue"
                style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: 8, border: '1px solid #d1d5db', fontSize: '0.85rem', color: '#1e293b', boxSizing: 'border-box' }}
              />
            </div>

            {/* Description */}
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: 4 }}>Description *</label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Describe what happened, what you expected, and steps to reproduce..."
                rows={4}
                style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: 8, border: '1px solid #d1d5db', fontSize: '0.85rem', color: '#1e293b', resize: 'vertical', boxSizing: 'border-box' }}
              />
            </div>

            {/* Screenshot Preview */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                📸 Auto-captured Screenshot
              </label>
              {screenshot ? (
                <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', maxHeight: 150 }}>
                  <img
                    src={screenshot}
                    alt="Screenshot preview"
                    style={{ width: '100%', height: 'auto', display: 'block' }}
                  />
                </div>
              ) : (
                <div style={{ background: '#f3f4f6', borderRadius: 8, padding: '0.75rem', textAlign: 'center', color: '#6b7280', fontSize: '0.8rem' }}>
                  Capturing screenshot...
                </div>
              )}
            </div>

            {/* Auto-captured metadata */}
            <div style={{ background: '#f8fafc', borderRadius: 8, padding: '0.5rem 0.75rem', marginBottom: '1rem', fontSize: '0.7rem', color: '#64748b' }}>
              <strong>Auto-captured:</strong> Page URL, Browser Info, Timestamp
            </div>

            {/* Error */}
            {error && (
              <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, padding: '0.5rem 0.75rem', marginBottom: '0.75rem', color: '#991b1b', fontSize: '0.8rem' }}>
                {error}
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={onClose}
                style={{ padding: '0.5rem 1rem', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer', fontWeight: 500, fontSize: '0.85rem' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                style={{
                  padding: '0.5rem 1.25rem', background: submitting ? '#94a3b8' : '#ef4444',
                  color: '#fff', border: 'none', borderRadius: 8, cursor: submitting ? 'not-allowed' : 'pointer',
                  fontWeight: 600, fontSize: '0.85rem',
                }}
              >
                {submitting ? 'Submitting...' : '🐛 Submit Report'}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
